import { Grid, Typography, Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, format, isWithinInterval } from 'date-fns';
import BookingTypeList from '../month/bookingTypeList';
import { useCalendar } from '../calendarContext'; 
import { useCalendarViews } from '../view/calendarViewsProvider';
import { useMemo } from 'react';
import { calendarMonthRange, calendarParts } from '../../../../../../platform/util/calendarSystem';

const RenderDays = ({ month, showEventDetailsInside, showEventMarker, mHeight, openings, selectedDayOnlyHighlight = false, todayCivil }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isCompactMarkerView = showEventMarker && !showEventDetailsInside;
    const {
        selectedDate, 
        toggleDate,
        displayCalendar = 'gregory',
    } = useCalendar();
    const {
        setView,
        view
    } = useCalendarViews() 

    const openingsByDate = useMemo(() => {
        if (!openings) return {};
        return openings.reduce((acc, curr) => {
            acc[curr.date] = curr.available.toLowerCase() === "yes";
            return acc;
        }, {});
    }, [openings]);


    const range = calendarMonthRange(format(month, 'yyyy-MM-dd'), displayCalendar);
    const startDate = new Date(`${range.start}T12:00:00`);
    const endDate = new Date(`${range.end}T12:00:00`);
    const startOfWeekDate = startOfWeek(startDate); // First day of the week (Sunday)
    const endOfWeekDate = endOfWeek(endDate); // Last day of the week (Saturday)
    const selectedWeekStart = selectedDate ? startOfWeek(selectedDate) : null;
    const selectedWeekEnd = selectedDate ? endOfWeek(selectedDate) : null;
    const HIGHLIGHT_BASE = '#1d4ed8';
    const HIGHLIGHT_BORDER = isDark ? '#7caaff' : 'rgba(29, 78, 216, 0.38)';
    const WEEK_HIGHLIGHT = isDark ? '#304a70' : 'rgba(29, 78, 216, 0.12)';
    const DAY_HIGHLIGHT = isDark ? '#365680' : 'rgba(29, 78, 216, 0.16)';
    const CELL_BORDER = theme.palette.divider;

    const days = eachDayOfInterval({ start: startOfWeekDate, end: endOfWeekDate });
    const sortedDays = days.sort((a, b) => a.getTime() - b.getTime()); // Sort days in ascending order

    // Combine the real days with the empty cells to make a full 6 rows
    const allDays = [...sortedDays];
    // Format the date for comparison.

    return (
        <Grid container sx={{ width: '100%' }}>
            {/* Render Weekday Names */}
            <Grid container>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <Grid item xs={12 / 7} key={index} align="center">
                        <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.secondary' }}>
                            {day}
                        </Typography>
                    </Grid>
                ))}
            </Grid>

            {/* Render the Days in the Month */}
            {allDays.map((day, ind) => {
                const formattedDate = format(day, 'yyyy-MM-dd');
                const isDayOpen = openings ? (!!openingsByDate[formattedDate]) : true;
                const isInCurrentMonth = formattedDate >= range.start && formattedDate <= range.end;
                if (!isInCurrentMonth && !isDayOpen) {
                    return (
                        <Grid
                            item
                            key={ind}
                            xs={12 / 7}
                            sx={{ 
                                boxSizing: 'border-box',
                                padding: '0px',
                                aspectRatio: '1',
                                height: mHeight ?? 'auto',
                            }}
                        />
                    );
                }

                const isSelectedDay = day && selectedDate && isSameDay(day, selectedDate); // Check if the day is selected
                const isTodayDay = todayCivil ? formattedDate === todayCivil : day && isToday(day);
                const isWeekMode = view === 'week';
                const isDayMode = view === 'day';
                const inSelectedWeek =
                    !!day &&
                    !!selectedWeekStart &&
                    !!selectedWeekEnd &&
                    isWithinInterval(day, { start: selectedWeekStart, end: selectedWeekEnd });
                const shouldHighlightWeekRow = !selectedDayOnlyHighlight && isWeekMode && inSelectedWeek;
                const shouldHighlightDayCell = isDayMode && isSelectedDay;
                const weekColumnIndex = ind % 7;
                const isWeekRowStart = shouldHighlightWeekRow && weekColumnIndex === 0;
                const isWeekRowEnd = shouldHighlightWeekRow && weekColumnIndex === 6;

                function getBackgroundColor(isSelectedDay, isInCurrentMonth, isDayOpen) {
                    if (shouldHighlightWeekRow) {
                        return WEEK_HIGHLIGHT;
                    } else if (shouldHighlightDayCell || (selectedDayOnlyHighlight && isSelectedDay)) {
                        return DAY_HIGHLIGHT;
                    } else if (isSelectedDay && !isWeekMode) {
                        return DAY_HIGHLIGHT;
                    } else if (isInCurrentMonth) { 
                        return isDayOpen ? theme.palette.background.paper : theme.palette.action.disabledBackground;
                    } else {
                        return alpha(theme.palette.text.primary, 0.04);
                    }
                }

                return (
                    <Grid
                        item
                        key={ind}
                        xs={12 / 7} // Ensure 7 equal columns for each day
                        onClick={(e) => {
                            if (!isDayOpen) return;
                            e.stopPropagation();
                            setView && setView(view === "month" || view === "year" ? "day" : view);
                            toggleDate(day);
                        }}
                        sx={{
                            textAlign: 'center',
                            background: getBackgroundColor(isSelectedDay, isInCurrentMonth, isDayOpen),
                            opacity: isDayOpen ? 1 : 0.3, // Lower opacity for grayed-out, non-available days
                            cursor: isDayOpen ? 'pointer' : 'not-allowed',
                            pointerEvents: isDayOpen ? 'auto' : 'none',
                            border: `1px solid ${CELL_BORDER}`,
                            borderTop: shouldHighlightWeekRow ? `2px solid ${HIGHLIGHT_BORDER}` : `1px solid ${CELL_BORDER}`,
                            borderBottom: shouldHighlightWeekRow ? `2px solid ${HIGHLIGHT_BORDER}` : `1px solid ${CELL_BORDER}`,
                            borderLeft: isWeekRowStart ? `2px solid ${HIGHLIGHT_BORDER}` : undefined,
                            borderRight: isWeekRowEnd ? `2px solid ${HIGHLIGHT_BORDER}` : undefined,
                            padding: isCompactMarkerView ? '2px 4px' : '4px',
                            boxSizing: 'border-box',
                            aspectRatio: '1',
                            height: mHeight ?? 'auto',
                            minHeight: isCompactMarkerView ? 44 : undefined,
                            overflow: 'hidden',
                            borderRadius: shouldHighlightWeekRow
                                ? (isWeekRowStart ? '8px 0 0 8px' : isWeekRowEnd ? '0 8px 8px 0' : '0')
                                : '0',
                            transition: 'background 0.2s, transform 0.2s',
                        }}
                    >
                        {day ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    height: '100%',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    pt: 0.1,
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Day Number at the Top Center */}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        background:
                                            (selectedDayOnlyHighlight && isSelectedDay) || isTodayDay || shouldHighlightDayCell
                                                ? HIGHLIGHT_BASE
                                                : 'transparent',
                                        color:
                                            (selectedDayOnlyHighlight && isSelectedDay) || isTodayDay || shouldHighlightDayCell
                                                ? '#fff'
                                                : isDark && shouldHighlightWeekRow ? '#f1f5ff' : 'text.primary',
                                        width: '25px',
                                        height: '25px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight:
                                            (selectedDayOnlyHighlight && isSelectedDay) || isTodayDay || shouldHighlightDayCell
                                                ? 'bold'
                                                : 'normal',
                                        border: selectedDayOnlyHighlight && isSelectedDay ? `2px solid ${HIGHLIGHT_BORDER}` : 'none',
                                        boxShadow: selectedDayOnlyHighlight && isSelectedDay ? '0 0 0 2px rgba(29, 78, 216, 0.18)' : 'none',
                                        flexShrink: 0,
                                        marginBottom: isCompactMarkerView ? '0px' : '4px',
                                        boxSizing: 'border-box',
                                        fontSize: '1rem',
                                    }}
                                >
                                    {calendarParts(formattedDate, displayCalendar).day}
                                </Typography>


                                {/* Event List Below the Day Number */}
                                <Box sx={{ width: '100%', textAlign: 'center', minWidth: 0, flexShrink: isCompactMarkerView ? 0 : 1, overflow: 'hidden' }}>
                                    <BookingTypeList
                                        date={day}
                                        showEventDetails={showEventDetailsInside}
                                        showEventMarker={showEventMarker} // or false, depending on your needs
                                        day={day}
                                        month={month} 
                                    /> 
                                </Box>
                            </Box>


                        ) : null} {/* Empty cell for non-month days */}
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default RenderDays;
